<script lang="ts">
    import { baseURL, socialMedia, websiteName, email } from "../../config"
    import { langField } from "../../i18n"
    import { location } from "../../store"
    export let createdAt: Date | string = new Date(),
        updatedAt: Date | string = new Date(),
        article = false,
        blog = false,
        FAQ = false,
        FAQDetails: {
            question: string
            answer: string
        }[] = [],
        metaDescription = "",
        title = "",
        product: TProduct

    createdAt = new Date(createdAt).toLocaleDateString("de-DE", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    })
    updatedAt = new Date(updatedAt).toLocaleDateString("de-DE", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    })

    const defaultProps = {
        author: {
            "@id": `${baseURL}/#company`,
        },
        publisher: {
            "@id": `${baseURL}/#company`,
        },
        inLanguage: {
            "@type": "Language",
            name: "German",
        },
        datePublished: createdAt,
        dateModified: updatedAt,
        isPartOf: {
            "@id": `${baseURL}/#website`,
        },
    }

    const schemaOrgEntity = {
        "@type": ["Store", "Organization"],
        "@id": `${baseURL}/#company`,
        name: websiteName,
        url: baseURL,
        email,

        image: {
            "@type": "ImageObject",
            "@id": `${baseURL}/#image`,
            inLanguage: {
                "@type": "Language",
                name: "German",
            },
            contentUrl: `${baseURL}/api/_/assets/logo/logo.png`,
            width: 576,
            height: 158,
            caption: "Renz Logo",
        },
        logo: {
            "@type": "ImageObject",
            "@id": `${baseURL}/#logo`,
            url: baseURL,
            contentUrl: `${baseURL}/api/_/assets/logo/logo.png`,
            caption: "Renz Logo",
            inLanguage: {
                "@type": "Language",
                name: "German",
            },
        },
        priceRange: "$$",
        location: {
            "@id": location,
        },
        sameAs: [socialMedia.instagram, socialMedia.facebook, socialMedia.linkedIn, socialMedia.xing],
    }

    const schemaOrgWebsite = {
        "@type": "WebSite",
        "@id": `${baseURL}/#website`,
        url: baseURL,
        name: "Renz",
        description: "Renz Online Store",
        ...defaultProps,
    }

    const schemaOrgWebPage = {
        "@type": "WebPage",
        "@id": `${$location.url}#webpage`,
        url: $location.url,
        name: title,
        ...defaultProps,
        description: metaDescription,

        potentialAction: [
            {
                "@type": "ReadAction",
                target: [$location.url],
            },
        ],
    }

    let schemaOrgArticle = null
    if (article || blog) {
        schemaOrgArticle = {
            "@type": "Article",
            "@id": `${$location.url}#article`,
            headline: title,
            description: metaDescription,
            ...defaultProps,
            articleSection: ["blog"],
        }
    }

    let schemaOrgBlog = null
    if (blog) {
        schemaOrgBlog = {
            "@type": "BlogPosting",
            "@id": `${$location.url}#blog`,
            headline: title,
            description: metaDescription,
            ...defaultProps,
        }
    }

    let schemaOrgFAQ = null
    if (FAQ) {
        schemaOrgFAQ = {
            "@type": "FAQPage",
            "@id": `${$location.url}#faq`,
            ...defaultProps,
            mainEntity: FAQDetails.map((faq) => ({
                "@type": "Question",
                name: faq.question,
                acceptedAnswer: {
                    "@type": "Answer",
                    text: faq.answer,
                },
            })),
        }
    }

    let schemaOrgProduct = null

    if (product) {
        schemaOrgProduct = {
            "@context": "http://schema.org/",
            "@type": "Product",
            "@id": `${$location.url}/#product`,
            ...defaultProps,
            aggregateRating: {
                "@type": "AggregateRating",
                ratingValue: product.averageRating,
                reviewCount: product.amountOfRatings,
            },
            review: [
                (product.ratings || []).map((rating) =>
                    !!rating
                        ? {
                              "@type": "Review",
                              author: rating.author,
                              datePublished: new Date(rating.review_date).toISOString().split("T")[0],
                              reviewBody: rating.comment || "No comment",
                              name: rating.comment || "No comment",
                              reviewRating: {
                                  "@type": "Rating",
                                  bestRating: "5",
                                  ratingValue: rating.rating,
                                  worstRating: "1",
                              },
                          }
                        : null
                ),
            ],
            image: {
                url: product.images[0]?.file?.src,
                caption: langField(product.name),
            },
            name: langField(product.name),
            description: product.description,
            gtin: product.gtin,
            brand: "Renz",
            category: product.categories[0],
            mainEntityOfPage: {
                "@id": `${$location.url}#webpage`,
            },
            releaseDate: createdAt,
            dateModified: updatedAt,
            url: $location.url,
            offers: {
                "@type": "Offer",
                availability: "http://schema.org/InStock",
                url: $location.url,
                price: product.price_net_end_customer,
                priceCurrency: product.price_net_end_customer,
                itemCondition: "NewCondition",
                seller: {
                    "@type": "Organization",
                    "@id": `${baseURL}/#company`,
                },
            },
        }
    }

    const schemaOrgArray = [
        schemaOrgEntity,
        schemaOrgWebsite,
        schemaOrgWebPage,
        schemaOrgProduct,
        schemaOrgFAQ,
        schemaOrgArticle,
        schemaOrgBlog,
    ].filter(Boolean)
    const schemaOrgObject = {
        "@context": "https://schema.org",
        "@graph": schemaOrgArray,
    }
    let jsonLdString = JSON.stringify(schemaOrgObject)
    let jsonLdScript = `
		<script type="application/ld+json">
			${jsonLdString}
		${"<"}/script>
	`
</script>

<svelte:head>
    {@html jsonLdScript}
</svelte:head>
